import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Callout,
  EmptyState,
  Icon,
  Illustration,
  Link,
  RowsSkeletonLoader,
} from '@node-space/ui-components'
import { PageHeader } from 'components/header/PageHeader'
import { PaymentLimitsTable } from 'components/PaymentLimits/PaymentLimitsTable'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { LimitLevel, PaymentLimitSheetType } from 'types/paymentLimits'
import { PaymentLimitsFilters } from './components/PaymentLimitsFilters'
import { PaymentLimitSheet } from './components/PaymentLimitSheet'
import { usePaymentLimitsFilters } from './hooks/usePaymentLimitsFilters'

export const PaymentLimits = () => {
  const { t } = useTranslation()
  const permissions = useMappedRoles()
  const canEditPaymentLimits = permissions?.paymentLimits?.canEdit ?? false

  const [isAddSheetOpen, setIsAddSheetOpen] = useState(false)

  const {
    filterProps,
    limits,
    totalElements,
    hasLimits,
    hasActiveFilters,
    isFetching,
    isError,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
  } = usePaymentLimitsFilters({ level: LimitLevel.ACCOUNT })

  const showFilters = hasLimits || hasActiveFilters || isFetching
  const showEmptyState = !isFetching && !isError && !hasLimits && !hasActiveFilters
  const showNoResults = !isFetching && !isError && !hasLimits && hasActiveFilters

  return (
    <>
      <PageHeader
        description={t('paymentLimits.pageDescription')}
        {...(canEditPaymentLimits && {
          renderRightSection: (
            <Button
              testId="add-limit-button"
              variant="secondary"
              iconElement={<Icon name="Plus" />}
              onClick={() => setIsAddSheetOpen(true)}
            >
              {t('paymentLimits.addLimit')}
            </Button>
          ),
        })}
      />

      <Box paddingB={24}>
        <Callout>
          <Box>
            {t('paymentLimits.customerLimitsCallout')}{' '}
            <Link href="/customers" target="_self">
              {t('paymentLimits.customerLimitsCalloutLink')} 
            </Link>
          </Box>
          {process?.env?.GLOBAL_LIMITS_HELP_CENTRE_URL && 
            <Link href={process.env.GLOBAL_LIMITS_HELP_CENTRE_URL} target="_blank">
            {t('paymentLimits.globalLimitsLinkText')}
            </Link>
          }
        </Callout>
      </Box>

      {showFilters && (
        <Box paddingB={24}>
          <PaymentLimitsFilters {...filterProps} />
        </Box>
      )}

      {isFetching && <RowsSkeletonLoader rows={3} />}

      {hasLimits && (
        <PaymentLimitsTable
          limits={limits}
          tableName="PaymentLimitsTable"
          page={page}
          rowsPerPage={rowsPerPage}
          totalElements={totalElements}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      )}

      {showNoResults && (
        <EmptyState
          testId="payment-limits-no-results"
          LottiePlayer={<Illustration name="NoData" />}
          title={t('paymentLimits.noResultsTitle', {
            defaultValue: 'No limits match your filters',
          })}
          description={t('paymentLimits.noResultsDescription', {
            defaultValue: 'Try adjusting or clearing your filters.',
          })}
        />
      )}

      {showEmptyState && (
        <EmptyState
          testId="payment-limits-empty-state"
          LottiePlayer={<Illustration name="Welcome" />}
          title={t('paymentLimits.emptyStateTitle')}
          description={t('paymentLimits.emptyStateDescription')}
          {...(canEditPaymentLimits && {
            PrimaryButton: (
              <Button
                variant="secondary"
                iconElement={<Icon name="Plus" />}
                onClick={() => setIsAddSheetOpen(true)}
              >
                {t('paymentLimits.addLimit')}
              </Button>
            ),
          })}
        />
      )}

      <PaymentLimitSheet
        isOpen={isAddSheetOpen}
        type={PaymentLimitSheetType.ADD}
        level={LimitLevel.ACCOUNT}
        onClose={() => setIsAddSheetOpen(false)}
      />
    </>
  )
}
